import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Checkers.css';

const BOARD_SIZE = 8;
const EMPTY = 0;
const RED_PIECE = 1;
const BLACK_PIECE = 2;
const RED_KING = 3;
const BLACK_KING = 4;

const Checkers = () => {
  const [board, setBoard] = useState(() => {
    const initialBoard = Array(BOARD_SIZE).fill().map(() => Array(BOARD_SIZE).fill(EMPTY));
    
    // Place initial pieces
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = BLACK_PIECE;
        }
      }
    }
    
    for (let row = 5; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = RED_PIECE;
        }
      }
    }
    
    return initialBoard;
  });
  
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(RED_PIECE);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  const startGame = () => {
    const initialBoard = Array(BOARD_SIZE).fill().map(() => Array(BOARD_SIZE).fill(EMPTY));
    
    // Place initial pieces
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = BLACK_PIECE;
        }
      }
    }
    
    for (let row = 5; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = RED_PIECE;
        }
      }
    }
    
    setBoard(initialBoard);
    setSelectedSquare(null);
    setCurrentPlayer(RED_PIECE);
    setGameActive(true);
    setGameOver(false);
    setWinner(null);
  };

  const isValidMove = (fromRow, fromCol, toRow, toCol) => {
    // Check if destination is empty and on dark square
    if (board[toRow][toCol] !== EMPTY || (toRow + toCol) % 2 === 0) {
      return false;
    }

    const piece = board[fromRow][fromCol];
    const rowDiff = toRow - fromRow;
    const colDiff = Math.abs(toCol - fromCol);

    // Check if it's a diagonal move
    if (Math.abs(rowDiff) !== colDiff) {
      return false;
    }

    // Check direction based on piece type
    if (piece === RED_PIECE && rowDiff > 0) return false; // Red moves up
    if (piece === BLACK_PIECE && rowDiff < 0) return false; // Black moves down

    // Simple move (one square)
    if (Math.abs(rowDiff) === 1) {
      return true;
    }

    // Jump move (two squares)
    if (Math.abs(rowDiff) === 2) {
      const middleRow = fromRow + rowDiff / 2;
      const middleCol = fromCol + (toCol - fromCol) / 2;
      const middlePiece = board[middleRow][middleCol];
      
      // Check if there's an opponent piece to jump over
      if (piece === RED_PIECE || piece === RED_KING) {
        return middlePiece === BLACK_PIECE || middlePiece === BLACK_KING;
      } else {
        return middlePiece === RED_PIECE || middlePiece === RED_KING;
      }
    }

    return false;
  };

  const makeMove = (fromRow, fromCol, toRow, toCol) => {
    const newBoard = board.map(row => [...row]);
    const piece = newBoard[fromRow][fromCol];
    
    // Move the piece
    newBoard[toRow][toCol] = piece;
    newBoard[fromRow][fromCol] = EMPTY;

    // Check for jump
    if (Math.abs(toRow - fromRow) === 2) {
      const middleRow = fromRow + (toRow - fromRow) / 2;
      const middleCol = fromCol + (toCol - fromCol) / 2;
      newBoard[middleRow][middleCol] = EMPTY;
    }

    // Check for king promotion
    if (piece === RED_PIECE && toRow === 0) {
      newBoard[toRow][toCol] = RED_KING;
    } else if (piece === BLACK_PIECE && toRow === BOARD_SIZE - 1) {
      newBoard[toRow][toCol] = BLACK_KING;
    }

    setBoard(newBoard);
    
    // Check for winner
    const redPieces = newBoard.flat().filter(p => p === RED_PIECE || p === RED_KING).length;
    const blackPieces = newBoard.flat().filter(p => p === BLACK_PIECE || p === BLACK_KING).length;
    
    if (redPieces === 0) {
      setWinner('Black');
      setGameOver(true);
      setGameActive(false);
    } else if (blackPieces === 0) {
      setWinner('Red');
      setGameOver(true);
      setGameActive(false);
    } else {
      // Switch players
      setCurrentPlayer(currentPlayer === RED_PIECE ? BLACK_PIECE : RED_PIECE);
    }
  };

  const handleSquareClick = (row, col) => {
    if (!gameActive) return;

    if (selectedSquare) {
      const [fromRow, fromCol] = selectedSquare;
      
      if (row === fromRow && col === fromCol) {
        // Deselect
        setSelectedSquare(null);
      } else if (isValidMove(fromRow, fromCol, row, col)) {
        // Make move
        makeMove(fromRow, fromCol, row, col);
        setSelectedSquare(null);
      } else {
        // Select new piece if it belongs to current player
        const piece = board[row][col];
        if ((currentPlayer === RED_PIECE && (piece === RED_PIECE || piece === RED_KING)) ||
            (currentPlayer === BLACK_PIECE && (piece === BLACK_PIECE || piece === BLACK_KING))) {
          setSelectedSquare([row, col]);
        } else {
          setSelectedSquare(null);
        }
      }
    } else {
      // Select piece if it belongs to current player
      const piece = board[row][col];
      if ((currentPlayer === RED_PIECE && (piece === RED_PIECE || piece === RED_KING)) ||
          (currentPlayer === BLACK_PIECE && (piece === BLACK_PIECE || piece === BLACK_KING))) {
        setSelectedSquare([row, col]);
      }
    }
  };

  const getPieceSymbol = (piece) => {
    switch (piece) {
      case RED_PIECE: return '🔴';
      case BLACK_PIECE: return '⚫';
      case RED_KING: return '👑';
      case BLACK_KING: return '🖤';
      default: return '';
    }
  };

  const getSquareClass = (row, col) => {
    let className = 'checker-square';
    
    if ((row + col) % 2 === 0) {
      className += ' light';
    } else {
      className += ' dark';
    }
    
    if (selectedSquare && selectedSquare[0] === row && selectedSquare[1] === col) {
      className += ' selected';
    }
    
    return className;
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🔴 Checkers</h1>
        <div className="game-instructions">
          Click to select and move pieces. Jump over opponent pieces to capture them!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Current Player: {currentPlayer === RED_PIECE ? 'Red 🔴' : 'Black ⚫'}
          {gameOver && <div style={{color: 'green'}}>Winner: {winner}!</div>}
        </div>
        
        <div className="game-play-area">
          <div className="checkers-board">
            {board.map((row, rowIndex) =>
              row.map((piece, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={getSquareClass(rowIndex, colIndex)}
                  onClick={() => handleSquareClick(rowIndex, colIndex)}
                >
                  {getPieceSymbol(piece)}
                </div>
              ))
            )}
          </div>
        </div>

        {gameOver && (
          <div className="game-over">
            <h2>🎉 Game Over!</h2>
            <p>Winner: {winner}</p>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Checkers;